import React, { useContext, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { ChoiceChip } from "../../../components/common/ChoiceChip";
import { Form, Formik } from "formik";
import ComboBox from "../../../components/common/ComboBox";
import { Button, SearchBox, TextBox } from "../../../components";
import AppContext from "../../../AppContext";
import { useNavigate } from "react-router-dom";
import useTendering from "../../../hooks/useTendering";
import useUsers from "../../../hooks/useUsers";
import useCompanies from "../../../hooks/useCompanies";
import TenderingLandingPageContext from "../../../contexts/TenderingLandingPageContext";
import TenderingCard from "../../../components/TenderingCard";
import { ReactComponent as EmptySvg } from "../../../assets/icons/empty-projects.svg";
import { provinces } from "../../../helpers/common.helper";

const TABS = {
  All: 0,
  Design: 1,
  Construction: 2,
};

const quickFilters = [
  {
    name: "All",
    value: TABS.All,
  },
  {
    name: "Design",
    value: TABS.Design,
  },
  {
    name: "Construction",
    value: TABS.Construction,
  },
];

const List = (props) => {
  const { designStyles } = useContext(AppContext);
  const { filter, setFilter } = useContext(TenderingLandingPageContext);
  const formikRef = useRef(null);
  const filterRef = useRef();
  const navigate = useNavigate();
  const [datasource, setDatasource] = useState([]);
  const { query } = useTendering();
  const { queryUsers } = useUsers();
  const { queryCompany } = useCompanies();
  const [initialValue, setInitialValue] = useState({
    category: TABS.All,
    status: undefined,
    search: "",
    work: undefined,
    space: undefined,
    styles: undefined,
    location: undefined,
    area: undefined,
  });

  const _getFilterPredicate = () => {
    let _filter = { active: true, deleted: false };

    for (const key in filter) {
      if (!Object.prototype.hasOwnProperty.call(filter, key)) continue;

      const element = filter[key];

      if (!element) continue;

      if (key === "category") {
        _filter.category = element;
        continue;
      }

      if (key === "status") {
        _filter.status = +element;
        continue;
      }

      if (key === "search") {
        _filter.name = { $regex: element };
        continue;
      }

      if (key === "work") {
        _filter.work = element;
        continue;
      }

      if (key === "space") {
        _filter.space = element;
        continue;
      }

      if (key === "styles") {
        _filter.styles = { $in: [element] };
        continue;
      }

      if (key === "location") {
        _filter.location = element;
        continue;
      }

      if (key === "area") {
        _filter["area.size"] = {
          $lte: +element,
        };
        continue;
      }

      if (key === "budget") {
        _filter.budget = {
          $lte: +element,
        };
        continue;
      }

      if (key === "date") {
        _filter["period.start"] = {
          $lte: element,
        };
        _filter["period.end"] = {
          $gte: element,
        };

        continue;
      }
    }

    return _filter;
  };

  const fetchData = async () => {
    // * Get filter predicate and query
    const { _metadata, data } = await query(
      _getFilterPredicate(filter),
      1,
      10,
      null,
      {
        createdOn: -1,
      }
    );

    // * Get owner
    const userIds = data.map((u) => u.createdBy);
    const companyIds = data.map((u) => u.company).filter((d) => !!d);

    const [userResults, companyResults] = await Promise.allSettled([
      queryUsers({ _id: { $in: userIds } }, null, -1, {
        _id: 1,
        firstname: 1,
        surname: 1,
        avatar: 1,
        uri: 1,
      }),
      queryCompany({ _id: { $in: companyIds } }, null, -1, {
        _id: 1,
        logo: 1,
        name: 1,
      }),
    ]);

    setDatasource(
      data.map((d) => {
        // * Map company
        if (d.company) {
          const _company = companyResults.value.data.find(
            (company) => company._id === d.company
          );

          d.createdBy = {
            isCompany: true,
            uri: _company._id,
            avatar: _company.logo,
            name: _company.name,
          };

          return d;
        }

        // * Map user
        const _user = userResults.value.data.find(
          (user) => user._id === d.createdBy
        );

        d.createdBy = {
          isCompany: false,
          uri: _user.uri,
          avatar: _user.avatar,
          name: _user.firstname + " " + _user.surname ?? "",
        };

        return d;
      })
    );
  };

  useEffect(() => {
    fetchData();
    setInitialValue(filter);
    filterRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [filter]);

  const handleValidate = (values) => {};

  const handleSubmit = (values, { setSubmitting }) => {
    setFilter(values);
    setSubmitting(false);
  };

  return (
    <div className="tendering__landing__list" ref={filterRef}>
      <Formik
        innerRef={formikRef}
        initialValues={initialValue}
        enableReinitialize={true}
        validate={handleValidate}
        onSubmit={handleSubmit}
      >
        {({ values, setFieldValue, submitForm }) => (
          <Form className="tendering__landing__list__filters">
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              {quickFilters.map((qf) => {
                const isActive = qf.value === Number(values.category);

                return (
                  <ChoiceChip
                    key={qf.value}
                    label={qf.name}
                    onClick={() => {
                      if (
                        qf.value === TABS.All &&
                        values.category === TABS.All
                      ) {
                        return;
                      }

                      if (values.category === qf.value) {
                        setFieldValue("category", TABS.All);
                        submitForm();
                        return;
                      }

                      setFieldValue("category", qf.value);
                      submitForm();
                    }}
                    isSelected={isActive}
                    width={80}
                    height={24}
                  />
                );
              })}
              <div style={{ flexGrow: 1 }} />
              <ComboBox name="status" placeholder={"All status"} options={[]} />
              <SearchBox
                name="search"
                placeholder="Project name"
                onChange={(val) => {
                  setFieldValue("search", val);
                  submitForm();
                }}
              />
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(6, minmax(0, 1fr))",
                gap: 12,
              }}
            >
              <ComboBox
                name="work"
                placeholder={"Scope of Work"}
                options={designStyles.works}
              />
              <ComboBox
                name="space"
                placeholder={"Type of Space"}
                options={designStyles.spaces}
              />
              <ComboBox
                name="work"
                placeholder={"Style"}
                options={designStyles.styles}
              />
              <ComboBox
                name="location"
                placeholder={"Location"}
                options={provinces}
              />
              <TextBox name="area" placeholder="Area" type="number" />
              <Button label={"Search"} type="submit" />
            </div>
          </Form>
        )}
      </Formik>
      <div className="tendering__landing__list__results">
        {datasource.map((data) => (
          <TenderingCard data={data} />
        ))}
        {datasource !== undefined && datasource.length === 0 && (
          <div className="tendering__landing__list__results__empty">
            <EmptySvg height={224} />
            <span>No results found</span>
          </div>
        )}
      </div>
    </div>
  );
};

List.propTypes = {};

export default List;
